import {useState, useEffect, useCallback} from "react";
import {useParams} from "react-router-dom";
import baseUrl from "../../baseUrl";
import {requester} from "../../services/requester";
import {
    decodeFileContent, parseDeclarationsCsv, downloadCsvTemplate, DECL_COLUMNS, CSV_DELIMITER,
} from "../../utils/csv";
import {useConfirm} from "../../contexts/ConfirmContext";

// Г4 — Сравнение на декларации-движение. Само сравнение между два файла;
// не пипа основната база. Резултатът се изчислява при всяко отваряне.
// Ж2: няколко реда с един и същ номер на имот са нормални — обединяват се в
// имот-група със сумирани площи, а сравнението е по ПЛОЩ, не по флаг.
const dka = (n) => (n === null || n === undefined ? "—" : Number(n).toFixed(3));

export const DeclarationsCompare = () => {
    const {settlement_ekatte} = useParams();
    const request = requester();
    const confirm = useConfirm();

    const [comparisons, setComparisons] = useState([]);
    const [filePrev, setFilePrev] = useState(null);
    const [fileCurr, setFileCurr] = useState(null);
    const [uploadError, setUploadError] = useState("");
    const [uploadIssues, setUploadIssues] = useState({errors: [], warnings: [], info: []});
    const [uploading, setUploading] = useState(false);
    const [compareData, setCompareData] = useState(null);
    const [active, setActive] = useState(null); // {year_prev, year_curr}

    const loadComparisons = useCallback(() => {
        request.get(`${baseUrl()}/declarations/${settlement_ekatte}`)
            .then(data => setComparisons(Array.isArray(data) ? data : []))
            .catch(() => setComparisons([]));
    }, [settlement_ekatte]);

    useEffect(() => {
        loadComparisons();
    }, [loadComparisons]);

    const loadCompare = (year_prev, year_curr) => {
        request.get(`${baseUrl()}/declarations/${settlement_ekatte}/compare?year_prev=${year_prev}&year_curr=${year_curr}`)
            .then(data => {
                setCompareData(data);
                setActive({year_prev, year_curr});
            })
            .catch(err => setUploadError(err.message || "Грешка при зареждане на сравнението."));
    };

    const readFile = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ({target}) => {
            const parsed = parseDeclarationsCsv(decodeFileContent(target.result));
            if (parsed.error) reject(new Error(parsed.error));
            else resolve(parsed.rows);
        };
        reader.onerror = () => reject(new Error("Грешка при четене на файла."));
        reader.readAsArrayBuffer(file);
    });

    const handleUpload = async () => {
        setUploadError("");
        setUploadIssues({errors: [], warnings: [], info: []});
        if (!filePrev || !fileCurr) {
            setUploadError("Изберете и двата файла (минала и текуща година).");
            return;
        }
        setUploading(true);
        try {
            const [rowsPrev, rowsCurr] = await Promise.all([readFile(filePrev), readFile(fileCurr)]);
            const yearsPrev = [...new Set(rowsPrev.map(r => r.year))];
            const yearsCurr = [...new Set(rowsCurr.map(r => r.year))];
            if (yearsPrev.length !== 1) throw new Error(`Файлът за минала година съдържа различни години: ${yearsPrev.join(", ")}.`);
            if (yearsCurr.length !== 1) throw new Error(`Файлът за текуща година съдържа различни години: ${yearsCurr.join(", ")}.`);
            const year_prev = yearsPrev[0];
            const year_curr = yearsCurr[0];
            if (year_prev === year_curr) throw new Error("Двата файла са за една и съща година.");

            const res = await request.post(`${baseUrl()}/declarations`, {
                settlement_ekatte, year_prev, year_curr,
                rows_prev: rowsPrev, rows_curr: rowsCurr,
            });
            // Е23/Ж2: успешното качване може да върне предупреждения (ЕКАТТЕ,
            // еднакви редове) и информация (обединени имоти).
            setUploadIssues({errors: [], warnings: res?.warnings || [], info: res?.info || []});
            setFilePrev(null);
            setFileCurr(null);
            loadComparisons();
            loadCompare(year_prev, year_curr);
        } catch (err) {
            // Е23: показваме ВСИЧКИ грешки/предупреждения (сървърът връща
            // {message, errors:[...], warnings:[...], info:[...]}).
            setUploadError(err.message || "Грешка при качването.");
            setUploadIssues({errors: err?.errors || [], warnings: err?.warnings || [], info: err?.info || []});
        } finally {
            setUploading(false);
        }
    };

    const deleteComparison = async (upload_id, yp, yc) => {
        if (!await confirm({
            title: 'Да се изтрие ли сравнението?',
            message: 'Качените декларации за този контекст се премахват; сравнението трябва да се направи наново.',
        })) return;
        try {
            await request.delete(`${baseUrl()}/declarations/upload/${upload_id}`);
            if (active && active.year_prev === yp && active.year_curr === yc) {
                setCompareData(null);
                setActive(null);
            }
            loadComparisons();
        } catch (err) {
            alert(err.message || "Грешка при изтриване.");
        }
    };

    const setResolution = async (rowIds, resolution) => {
        try {
            await Promise.all(rowIds.map(id =>
                request.put(`${baseUrl()}/declarations/rows/${id}/resolution`, {resolution})));
            if (active) loadCompare(active.year_prev, active.year_curr);
        } catch (err) {
            alert(err.message || "Грешка при записване на решението.");
        }
    };

    // „Класификация" (старото „Желание") — стойността на participates.
    const classif = (v) => v === null || v === undefined ? "смесена" : (v ? "За разпр." : "Не участва");

    const issuesBlock = (uploadIssues.errors.length > 0 || uploadIssues.warnings.length > 0
        || uploadIssues.info.length > 0);

    return (
        <div className="mt-3">
            <h5 style={{color: "var(--app-gold)"}}>Сравнение на декларации-движение</h5>
            <div className="text-muted mb-2" style={{fontSize: "0.9rem"}}>
                Само сравнение между два файла (минала и текуща стопанска година) — не пипа основната база.
                Ново качване за същата двойка години заменя предишното.
                Няколко реда с един и същ номер на имот се обединяват и площите им се сумират.
            </div>

            {/* Качване */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-auto">
                            <label className="form-label">Декларации — минала година:</label>
                            <input type="file" accept=".csv" className="form-control"
                                   onChange={e => setFilePrev(e.target.files[0] || null)}/>
                        </div>
                        <div className="col-auto">
                            <label className="form-label">Декларации — текуща година:</label>
                            <input type="file" accept=".csv" className="form-control"
                                   onChange={e => setFileCurr(e.target.files[0] || null)}/>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-secondary" onClick={handleUpload}
                                    disabled={uploading || !filePrev || !fileCurr}>
                                {uploading ? "Качване..." : "Сравни"}
                            </button>
                        </div>
                    </div>
                    <div className="text-muted mt-2" style={{fontSize: "0.85rem"}}>
                        Шапка (първи ред): Класификация, Желание, Година, ЕКАТТЕ, Номер на имот, Площ, Булстат, Ползвател.
                        <br/>
                        „Класификация": „За разпределяне" / „Не участва в разпределянето".{" "}
                        „Желание": „Обработка в реални граници" / „За участие в споразумение".
                        <br/>
                        Разделител — точка и запетая или запетая (разпознава се автоматично).
                    </div>
                    <button type="button" className="btn btn-outline-secondary btn-sm mt-2"
                            onClick={() => downloadCsvTemplate(DECL_COLUMNS.join(CSV_DELIMITER), "shablon-deklaracii.csv")}>
                        ⬇ Свали шаблон (.csv)
                    </button>
                    {uploadError && <div className="text-danger mt-2">{uploadError}</div>}
                    {issuesBlock && (
                        <div className="mt-2 border rounded p-2" style={{maxHeight: "260px", overflowY: "auto"}}>
                            {uploadIssues.errors.length > 0 && (
                                <div className="mb-2">
                                    <div className="fw-bold text-danger">Грешки: {uploadIssues.errors.length}</div>
                                    <ul className="mb-0 small text-danger">
                                        {uploadIssues.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                            {uploadIssues.warnings.length > 0 && (
                                <div className="mb-2">
                                    <div className="fw-bold text-warning-emphasis">
                                        Предупреждения: {uploadIssues.warnings.length}
                                    </div>
                                    <ul className="mb-0 small text-muted">
                                        {uploadIssues.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                    </ul>
                                </div>
                            )}
                            {uploadIssues.info.length > 0 && (
                                <div>
                                    <div className="fw-bold text-secondary">Информация</div>
                                    <ul className="mb-0 small text-muted">
                                        {uploadIssues.info.map((m, i) => <li key={i}>{m}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Налични сравнения */}
            {comparisons.length > 0 && (
                <div className="mb-3">
                    <h6>Налични сравнения:</h6>
                    <ul className="list-group">
                        {comparisons.map(c => (
                            <li key={c.upload_id}
                                className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <button className="btn btn-link p-0"
                                        onClick={() => loadCompare(c.year_prev, c.year_curr)}>
                                    {c.year_prev} → {c.year_curr}
                                </button>
                                <span className="text-muted" style={{fontSize: "0.85rem"}}>
                                    {c.create_on} · {c.user}
                                </span>
                                <button className="btn btn-outline-danger btn-sm"
                                        onClick={() => deleteComparison(c.upload_id, c.year_prev, c.year_curr)}>
                                    Изтрий
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {compareData && (
                <div>
                    <h5 className="mt-3" style={{color: "var(--app-gold)"}}>
                        Резултат: {compareData.upload.year_prev} → {compareData.upload.year_curr}
                    </h5>

                    {compareData.pending.count > 0 && (
                        <div className="alert alert-warning">
                            {compareData.pending.count} имота чакат решение за разделяне/промяна — виж „Несъпоставени имоти" по-долу.
                        </div>
                    )}

                    {/* СД-1-К — по Класификация */}
                    <h6 className="mt-3 fw-bold">СД-1-К — смяна на класификация (разлика в участващата площ)</h6>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="fw-bold">Влизат в разпределението ({dka(compareData.sd1.entering_total)})</div>
                            <Sd1Table rows={compareData.sd1.entering}/>
                        </div>
                        <div className="col-md-6">
                            <div className="fw-bold">Излизат ({dka(compareData.sd1.leaving_total)})</div>
                            <Sd1Table rows={compareData.sd1.leaving}/>
                        </div>
                    </div>

                    {/* СД-1-Ж — по Желание */}
                    {compareData.sd1_wish && (
                        <>
                            <h6 className="mt-3 fw-bold">СД-1-Ж — смяна на желание</h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="fw-bold">
                                        Минават „За участие в споразумение" ({dka(compareData.sd1_wish.to_agreement_total)})
                                    </div>
                                    <WishTable rows={compareData.sd1_wish.to_agreement}/>
                                </div>
                                <div className="col-md-6">
                                    <div className="fw-bold">
                                        Минават „Обработка в реални граници" ({dka(compareData.sd1_wish.to_real_total)})
                                    </div>
                                    <WishTable rows={compareData.sd1_wish.to_real}/>
                                </div>
                            </div>
                        </>
                    )}

                    {/* СД-2 */}
                    <h6 className="mt-3 fw-bold">СД-2 — по декларатор</h6>
                    {compareData.sd2.length === 0 && <div className="text-muted">Няма движения по декларатор.</div>}
                    {compareData.sd2.map(d => (
                        <div key={d.bullstat} className="card mb-2">
                            <div className="card-body py-2">
                                <div className="fw-bold">{d.declarator_name} <span className="text-muted">({d.bullstat})</span></div>
                                {d.new.length > 0 && (
                                    <div className="mt-1">
                                        <span className="text-success">Нови имоти ({dka(d.new_total)}):</span>
                                        <ul className="mb-1">
                                            {d.new.map((it, i) => (
                                                <li key={i}>{it.property_number} — {dka(it.area)}, от: {it.from}
                                                    {it.participates_prev !== undefined && (
                                                        <span className="text-muted"> [класификация: {classif(it.participates_prev)} → {classif(it.participates_curr)}]</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {d.missing.length > 0 && (
                                    <div className="mt-1">
                                        <span className="text-danger">Липсващи имоти ({dka(d.missing_total)}):</span>
                                        <ul className="mb-0">
                                            {d.missing.map((it, i) => (
                                                <li key={i}>{it.property_number} — {dka(it.area)}, {it.to}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Обединени / смесени имоти — за ръчна корекция.
                        Ж7: прегледаните се скриват в свито меню, за да останат
                        накрая видими само СД-1 и СД-2. */}
                    {compareData.merged && compareData.merged.count > 0 && (
                        <>
                            <h6 className="mt-3 fw-bold">
                                Обединени / смесени имоти ({compareData.merged.count})
                            </h6>
                            <div className="text-muted mb-2" style={{fontSize: "0.85rem"}}>
                                Имоти с повече от един ред в някоя от годините или с различни стойности между
                                редовете. Площите вече са сумирани; тук се преглеждат и се приемат или изключват.
                            </div>
                            {compareData.merged.items.map(it => (
                                <MergedItem key={it.property_number} item={it} setResolution={setResolution}/>
                            ))}
                        </>
                    )}

                    {compareData.merged && compareData.merged.resolved.length > 0 && (
                        <Collapsed title={`Обработени обединени/смесени имоти (${compareData.merged.resolved.length})`}>
                            {compareData.merged.resolved.map(it => (
                                <MergedItem key={it.property_number} item={it} setResolution={setResolution}/>
                            ))}
                        </Collapsed>
                    )}

                    {/* Несъпоставени (pending) */}
                    {compareData.pending.count > 0 && (
                        <>
                            <h6 className="mt-3 fw-bold">Несъпоставени имоти (чакат решение)</h6>
                            <PendingList title="От миналата година" rows={compareData.pending.prev}
                                         setResolution={setResolution} classif={classif}/>
                            <PendingList title="От текущата година" rows={compareData.pending.curr}
                                         setResolution={setResolution} classif={classif}/>
                        </>
                    )}

                    {compareData.pending.resolved && compareData.pending.resolved.length > 0 && (
                        <Collapsed title={`Обработени несъпоставени имоти (${compareData.pending.resolved.length})`}>
                            {compareData.pending.resolved.map(r => (
                                <ResolvedPending key={r.property_number} row={r}
                                                 setResolution={setResolution}/>
                            ))}
                        </Collapsed>
                    )}
                </div>
            )}
        </div>
    );
};

// Ж7: свито меню за вече обработените имоти — минават се веднъж и не пречат.
const Collapsed = ({title, children}) => (
    <details className="mt-2 mb-3">
        <summary className="text-muted" style={{cursor: "pointer", fontSize: "0.9rem"}}>
            {title}
        </summary>
        <div className="mt-2">{children}</div>
    </details>
);

const RESOLUTION_LABEL = {include: "прибавен", ignore: "изключен"};

const ResolvedPending = ({row, setResolution}) => (
    <div className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div style={{fontSize: "0.9rem"}}>
            <strong>{row.property_number}</strong> — {dka(row.area)} ·{" "}
            {row.kind === "prev" ? "от миналата година" : "от текущата година"} · {row.declarator}
            <span className="badge bg-secondary ms-2">
                {RESOLUTION_LABEL[row.resolution] || row.resolution}
            </span>
        </div>
        <button className="btn btn-outline-secondary btn-sm"
                onClick={() => setResolution(row.row_ids, null)}>
            Върни за преглед
        </button>
    </div>
);

const Sd1Table = ({rows}) => {
    if (rows.length === 0) return <div className="text-muted mb-2">—</div>;
    return (
        <div className="table-responsive">
            <table className="table table-sm table-bordered">
                <thead className="tableHead">
                <tr><th>Имот</th><th className="text-end">Разлика</th><th>Декларатор (мин.→тек.)</th></tr>
                </thead>
                <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td>
                            {r.property_number}
                            {(r.row_count_prev > 1 || r.row_count_curr > 1) && (
                                <span className="badge bg-secondary ms-1"
                                      title="Имотът е записан на няколко реда — площите са сумирани">
                                    {r.row_count_prev} / {r.row_count_curr} реда
                                </span>
                            )}
                            {r.partial && (
                                <span className="badge bg-info text-dark ms-1"
                                      title="Само част от имота сменя класификацията">частично</span>
                            )}
                            {/* Е5.1: за излизащите — желанието на излизащата част + аномалия */}
                            {r.wish_curr && (
                                <span className="text-muted d-block" style={{fontSize: "0.8rem"}}>
                                    желание: {r.wish_curr}
                                </span>
                            )}
                            {r.anomaly && (
                                <span className="badge bg-danger ms-1"
                                      title={'Очаква се „Обработка в реални граници" или разделяне на имота'}>
                                    провери
                                </span>
                            )}
                        </td>
                        <td className="text-end">
                            {dka(r.area)}
                            <span className="text-muted d-block" style={{fontSize: "0.8rem"}}>
                                участва: {dka(r.area_part_prev)} → {dka(r.area_part_curr)}
                            </span>
                            {r.area_prev !== undefined && (
                                <span className="text-muted d-block" style={{fontSize: "0.8rem"}}>
                                    общо: {dka(r.area_prev)} → {dka(r.area_total_curr)}
                                </span>
                            )}
                        </td>
                        <td>{r.declarator_prev} → {r.declarator_curr}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const WishTable = ({rows}) => {
    if (rows.length === 0) return <div className="text-muted mb-2">—</div>;
    return (
        <div className="table-responsive">
            <table className="table table-sm table-bordered">
                <thead className="tableHead">
                <tr><th>Имот</th><th className="text-end">Разлика</th><th>Декларатор</th></tr>
                </thead>
                <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td>{r.property_number}</td>
                        <td className="text-end">
                            {dka(r.area)}
                            <span className="text-muted d-block" style={{fontSize: "0.8rem"}}>
                                {dka(r.area_prev)} → {dka(r.area_curr)}
                            </span>
                        </td>
                        <td>{r.declarator_curr}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

// Разбивка на редовете на един имот за една година.
const SideDetails = ({title, side}) => {
    if (!side) return <div className="col-md-6 text-muted">{title}: няма</div>;
    return (
        <div className="col-md-6">
            <div className="fw-bold" style={{fontSize: "0.9rem"}}>
                {title}: {dka(side.area_total)} ({side.row_count} реда), участва {dka(side.area_part)}
            </div>
            <ul className="mb-1 small text-muted">
                {side.details.map(d => (
                    <li key={d.row_id}>
                        {dka(d.area)} · {d.participates ? "За разпр." : "Не участва"} · {d.wish || "—"} · {d.declarator_name}
                        {d.resolution && <span className="badge bg-secondary ms-1">{d.resolution === "ignore" ? "изключен" : "приет"}</span>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const MergedItem = ({item, setResolution}) => (
    <div className="border rounded p-2 mb-2">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
                <strong>{item.property_number}</strong>{" "}
                <span className="text-muted" style={{fontSize: "0.85rem"}}>({item.reasons.join(", ")})</span>
                {item.resolved && <span className="badge bg-success ms-2">прегледан</span>}
            </div>
            <div className="d-flex gap-2 flex-wrap">
                <button className="btn btn-outline-success btn-sm"
                        onClick={() => setResolution(item.row_ids, "include")}>
                    Приеми
                </button>
                <button className="btn btn-outline-secondary btn-sm"
                        onClick={() => setResolution(item.row_ids, "ignore")}>
                    Изключи от сравнението
                </button>
                {item.resolved && (
                    <button className="btn btn-outline-secondary btn-sm"
                            onClick={() => setResolution(item.row_ids, null)}>
                        Върни за преглед
                    </button>
                )}
            </div>
        </div>
        <div className="row mt-1">
            <SideDetails title="Минала година" side={item.prev}/>
            <SideDetails title="Текуща година" side={item.curr}/>
        </div>
    </div>
);

const PendingList = ({title, rows, setResolution, classif}) => {
    if (rows.length === 0) return null;
    return (
        <div className="mb-2">
            <div className="fw-bold">{title}:</div>
            {rows.map(r => (
                <div key={r.property_number} className="border rounded p-2 mb-2">
                    <div>
                        <strong>{r.property_number}</strong> — {dka(r.area)}
                        {r.row_count > 1 && (
                            <span className="badge bg-secondary ms-1"
                                  title="Обединени редове">{r.row_count} реда</span>
                        )}
                        {" "}· класификация: {classif(r.participates)} · желание: {r.wish || "смесено"} ·
                        декларатор: {r.declarator}
                    </div>
                    {r.suggestions.length > 0 && (
                        <div className="mt-1" style={{fontSize: "0.9rem"}}>
                            {r.suggestions.map((s, i) => (
                                <div key={i} className="d-flex align-items-center flex-wrap gap-2 mt-1">
                                    <span className="text-muted">
                                        {s.same_property
                                            ? "Вероятно същият имот с друг запис на номера: "
                                            : "Подсказка: "}
                                        {s.parts.map(p => p.property_number).join(" + ")} = {dka(s.sum)}
                                        {" "}(разлика {dka(s.diff)})
                                    </span>
                                    <button className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setResolution(
                                                [...r.row_ids, ...s.parts.flatMap(p => p.row_ids)], "ignore")}>
                                        {s.same_property ? "Приеми (един и същ имот)" : "Приеми разделянето"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Ж7: имотът няма насрещен ползвател — бил е или става бели
                        петна. Затова в СД-2 срещу него се изписва „БП". Кой от
                        двата случая е, се определя от годината, в която имотът
                        липсва. */}
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                        <button className="btn btn-outline-success btn-sm"
                                onClick={() => setResolution(r.row_ids, "include")}>
                            {r.kind === "prev"
                                ? "Прибави (отпаднал имот — БП)"
                                : "Прибави (нов имот — БП)"}
                        </button>
                        <button className="btn btn-outline-secondary btn-sm"
                                onClick={() => setResolution(r.row_ids, "ignore")}>
                            Игнорирай
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};
